import React from 'react';
import { Typography, TypographyProps, SxProps, Theme } from '@mui/material';
import { colors, typographyVariants } from '../../tokens';

export interface CaptionProps extends Omit<TypographyProps, 'variant'> {
  /**
   * The content to display
   */
  children: React.ReactNode;
  /**
   * Color variant
   * @default 'tertiary'
   */
  colorVariant?: 'primary' | 'secondary' | 'tertiary' | 'muted';
  /**
   * Custom styles to apply
   */
  sx?: SxProps<Theme>;
}

/**
 * Caption - Small text for supplementary information
 * 
 * Use cases:
 * - Timestamps and dates
 * - Helper text
 * - Metadata and details
 * - Footnotes
 * - Image captions
 * - File sizes and technical info
 * 
 * @example
 * ```tsx
 * <Caption>Created 2 hours ago</Caption>
 * <Caption colorVariant="tertiary">Last edited Jan 19, 2026</Caption>
 * <Caption colorVariant="muted">3.2 MB</Caption>
 * ```
 */
export const Caption: React.FC<CaptionProps> = ({ 
  children, 
  colorVariant = 'tertiary',
  sx = {}, 
  ...props 
}) => {
  const colorMap = {
    primary: colors.text.primary,
    secondary: colors.text.secondary,
    tertiary: colors.text.tertiary,
    muted: colors.text.muted,
  };

  return (
    <Typography
      sx={{
        fontFamily: typographyVariants.caption.fontFamily,
        fontWeight: typographyVariants.caption.fontWeight,
        fontSize: typographyVariants.caption.fontSize,
        lineHeight: typographyVariants.caption.lineHeight,
        color: colorMap[colorVariant],
        ...sx,
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};
